import type { MapDocument } from "@maps/schema";

const API_BASE = "/api";

function getToken(): string | null {
  return localStorage.getItem("maps-token");
}

export function setToken(token: string): void {
  localStorage.setItem("maps-token", token);
}

async function api<T>(
  path: string,
  opts: RequestInit = {},
): Promise<T> {
  const token = getToken();
  const headers: Record<string, string> = {
    ...(opts.headers as Record<string, string>),
  };
  if (opts.body) headers["content-type"] = "application/json";
  if (token) headers.authorization = `Bearer ${token}`;

  const res = await fetch(`${API_BASE}${path}`, { ...opts, headers });
  if (!res.ok) {
    let message = res.statusText;
    try {
      const body = (await res.json()) as { message?: string };
      if (body.message) message = body.message;
    } catch {
      // use statusText
    }
    throw new Error(`API ${res.status}: ${message}`);
  }
  return res.json() as Promise<T>;
}

export async function devLogin(): Promise<{ token: string; userId: string }> {
  const res = await api<{ token: string; userId: string }>(
    "/auth/dev-login",
    { method: "POST" },
  );
  setToken(res.token);
  return res;
}

export interface MapSummary {
  id: string;
  name: string;
  revision: number;
  isPublished: boolean;
  updatedAt: string;
  createdAt: string;
}

export async function listMaps(): Promise<MapSummary[]> {
  const res = await api<{ maps: MapSummary[] }>("/maps");
  return res.maps;
}

export async function getMap(
  id: string,
): Promise<{ map: MapSummary; document: MapDocument }> {
  return api<{ map: MapSummary; document: MapDocument }>(`/maps/${id}`);
}

export async function createMap(
  doc: MapDocument,
): Promise<{ id: string; revision: number }> {
  const res = await api<{ map: { id: string; revision: number } }>("/maps", {
    method: "POST",
    body: JSON.stringify(doc),
  });
  return res.map;
}

export async function updateMap(
  id: string,
  doc: MapDocument,
): Promise<{ revision: number }> {
  const res = await api<{ map: { revision: number } }>(`/maps/${id}`, {
    method: "PUT",
    body: JSON.stringify(doc),
  });
  return res.map;
}

export async function deleteMap(id: string): Promise<void> {
  await api(`/maps/${id}`, { method: "DELETE" });
}